
/**
 * Splits a URL into segments and removes empty strings
 * @param {string} url - The URL to parse
 * @returns {string[]} Array of URL segments
 */
export const parseUrlSegments = (url) => url.split('/').filter(Boolean);

/**
 * Creates a matcher function for comparing URL segments against expected patterns
 * @param {string[]} expectedSegments - Array of expected segments, where '*' matches any value
 * @returns {function(string): boolean} Function that returns true if URL matches pattern
 */
export const createPathMatcher = (expectedSegments) => (url) => {
  const segments = parseUrlSegments(url);
  return segments.length === expectedSegments.length &&
    expectedSegments.every((expected, index) =>
      expected === '*' || segments[index] === expected
    );
};

/**
 * Determines the current page based on URL pattern matching.
 * In test environments, the page can be overridden using window.__TEST_PAGE__.
 * This is useful when testing components that require specific page contexts
 * but are mounted without a real URL path.
 *
 * @param {Object[]} patterns - Array of page patterns to match against
 * @param {string} defaultPage - Default page to return if no match is found
 * @returns {string} The matched pattern, test page override, or default page as fallback
 *
 * @example
 * // Normal usage
 * const page = getPageFromUrl(PAGE_PATTERNS, "edit_feed");
 *
 * @example
 * // In test environment
 * window.__TEST_PAGE__ = "feeds";
 * const page = getPageFromUrl(PAGE_PATTERNS, "edit_feed"); // Returns "feeds"
 */
export const getPageFromUrl = (patterns, defaultPage) => {
  if (typeof window !== 'undefined' && window.__TEST_PAGE__) {
    return window.__TEST_PAGE__;
  }

  const currentUrl = typeof window !== 'undefined'
    ? window.location.pathname
    : '';

  const matchedPage = patterns.find(
    ({ matcher }) => matcher(currentUrl)
  );

  return matchedPage?.pattern ?? defaultPage;
};
